import { HydratedAsset, TrashIndexSort } from "@zapier/bff";
import { AssetTypes } from "@zapier/utils";
import { SortDescriptor } from "@zapier/design-system";
import type { FolderTreeItem } from "../folderTree/useQueryFolderTree";

export type AssetSortField =
  | "name"
  | "lastModified"
  | "status"
  | "location"
  | undefined;
export type AssetSortDirection = "asc" | "desc";

export interface AssetSortState {
  field: AssetSortField;
  direction: AssetSortDirection;
}

// "location" only sorts on the trash index path, where the server does the work;
// `sortAssets` has no in-memory counterpart for it.
const SORTABLE_COLUMNS = new Set<string>([
  "name",
  "lastModified",
  "status",
  "location",
] satisfies NonNullable<AssetSortField>[]);

/**
 * Calculates the next sort state based on the current state and sort descriptor.
 * Toggles between ascending and descending when the same column is clicked again.
 */
export function getNextSortState(
  currentState: AssetSortState,
  sortDescriptor: SortDescriptor,
): AssetSortState {
  const column = String(sortDescriptor.column);

  if (!SORTABLE_COLUMNS.has(column)) {
    return currentState;
  }

  if (currentState.field === column) {
    return {
      field: column as AssetSortField,
      direction: currentState.direction === "asc" ? "desc" : "asc",
    };
  }

  const tableDirection: AssetSortDirection =
    sortDescriptor.direction === "ascending" ? "asc" : "desc";

  return { field: column as AssetSortField, direction: tableDirection };
}

const TRASH_INDEX_SORT_FIELDS: Partial<
  Record<
    NonNullable<AssetSortField>,
    Record<AssetSortDirection, TrashIndexSort>
  >
> = {
  name: { asc: "asset_name", desc: "-asset_name" },
  lastModified: { asc: "deleted_at", desc: "-deleted_at" },
  location: { asc: "folder_name", desc: "-folder_name" },
};

/** Maps a table sort state onto the trash index `sort` value, if it has one. */
export function toTrashIndexSort({
  field,
  direction,
}: AssetSortState): TrashIndexSort | undefined {
  if (!field) return undefined;
  return TRASH_INDEX_SORT_FIELDS[field]?.[direction];
}

/**
 * Gets the display name for a hydrated asset.
 * Zaps use 'title', all other asset types use 'name'.
 */
function getAssetDisplayName(asset: HydratedAsset): string {
  if (asset.type === AssetTypes.Zap && "title" in asset) {
    return (asset.title as string)?.toLowerCase() || "";
  }
  if ("name" in asset) {
    return (asset.name as string)?.toLowerCase() || "";
  }
  return "";
}

/**
 * Gets the last modified date for a hydrated asset.
 * Zaps use 'updatedAt', other assets use 'lastUpdatedAt'.
 */
function getAssetLastModified(asset: HydratedAsset): number {
  if (asset.type === AssetTypes.Zap && "updatedAt" in asset) {
    return new Date(asset.updatedAt as string).getTime() || 0;
  }
  if ("lastUpdatedAt" in asset && asset.lastUpdatedAt) {
    return new Date(asset.lastUpdatedAt as string).getTime() || 0;
  }
  return 0;
}

/** Gets when a hydrated asset was trashed, or 0 if it isn't deleted. */
function getAssetDeletedAt(asset: HydratedAsset): number {
  if ("deletedAt" in asset && asset.deletedAt) {
    return new Date(asset.deletedAt as string).getTime() || 0;
  }
  return 0;
}

/**
 * Sorts hydrated assets by name field.
 * Zaps use 'title', all other asset types use 'name'.
 */
export function sortAssetsByName(
  assets: HydratedAsset[],
  direction: AssetSortDirection = "asc",
): HydratedAsset[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...assets].sort((a, b) => {
    const nameA = getAssetDisplayName(a);
    const nameB = getAssetDisplayName(b);
    return nameA.localeCompare(nameB) * multiplier;
  });
}

/**
 * Sorts hydrated assets by last modified date.
 */
export function sortAssetsByLastModified(
  assets: HydratedAsset[],
  direction: AssetSortDirection = "desc",
): HydratedAsset[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...assets].sort((a, b) => {
    const dateA = getAssetLastModified(a);
    const dateB = getAssetLastModified(b);
    return (dateA - dateB) * multiplier;
  });
}

/**
 * Sorts hydrated assets by when they were trashed. Only meaningful for the
 * Trash view's "Deleted" column, which reuses the "lastModified" sort key.
 */
export function sortAssetsByDeletedAt(
  assets: HydratedAsset[],
  direction: AssetSortDirection = "desc",
): HydratedAsset[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...assets].sort((a, b) => {
    const dateA = getAssetDeletedAt(a);
    const dateB = getAssetDeletedAt(b);
    return (dateA - dateB) * multiplier;
  });
}

const TOGGLEABLE_ASSET_TYPES = new Set<string>([
  AssetTypes.Zap,
  AssetTypes.Agent,
]);

function hasToggle(asset: HydratedAsset): boolean {
  return TOGGLEABLE_ASSET_TYPES.has(asset.type);
}

function isAssetOn(asset: HydratedAsset): boolean {
  if ("isEnabled" in asset) {
    return asset.isEnabled === true;
  }
  return false;
}

/**
 * Returns a status rank for sorting. Lower rank = higher priority.
 *
 * "asc" (On first):  On=0, Off=1, No toggle=2
 * "desc" (Off first): Off=0, On=1, No toggle=2
 */
function getStatusRank(
  asset: HydratedAsset,
  direction: AssetSortDirection,
): number {
  if (!hasToggle(asset)) return 2;

  const isOn = isAssetOn(asset);

  if (direction === "asc") {
    return isOn ? 0 : 1;
  }
  return isOn ? 1 : 0;
}

/**
 * Sorts hydrated assets by status (on/off toggle).
 *
 * "asc" = On first, then Off, then assets without a toggle.
 * "desc" = Off first, then On, then assets without a toggle.
 *
 * Within each group, assets are sorted alphabetically A→Z by name.
 */
export function sortAssetsByStatus(
  assets: HydratedAsset[],
  direction: AssetSortDirection = "asc",
): HydratedAsset[] {
  return [...assets].sort((a, b) => {
    const rankA = getStatusRank(a, direction);
    const rankB = getStatusRank(b, direction);

    if (rankA !== rankB) return rankA - rankB;

    const nameA = getAssetDisplayName(a);
    const nameB = getAssetDisplayName(b);
    return nameA.localeCompare(nameB);
  });
}

/**
 * Sorts hydrated assets by the specified field. In the Trash view,
 * `isTrashView` routes the "lastModified" field ("Deleted" column there) to
 * sort by trash time instead of last content edit.
 */
export function sortAssets(
  assets: HydratedAsset[],
  field: AssetSortField,
  direction: AssetSortDirection,
  isTrashView = false,
): HydratedAsset[] {
  if (field === "name") {
    return sortAssetsByName(assets, direction);
  }
  if (field === "lastModified") {
    return isTrashView
      ? sortAssetsByDeletedAt(assets, direction)
      : sortAssetsByLastModified(assets, direction);
  }
  if (field === "status") {
    return sortAssetsByStatus(assets, direction);
  }
  return assets;
}

/**
 * Filters hydrated assets whose display name contains the search query.
 * Uses the same name resolution as sorting (Zaps use 'title', others use 'name').
 */
export function filterAssetsBySearch(
  assets: HydratedAsset[],
  searchQuery: string,
): HydratedAsset[] {
  const query = searchQuery.trim().toLowerCase();
  if (!query) return assets;
  return assets.filter((asset) => getAssetDisplayName(asset).includes(query));
}

/**
 * Filters folder tree items whose name contains the search query.
 * Counterpart of filterAssetsBySearch for subfolder search.
 */
export function filterSubfoldersBySearch(
  subfolders: FolderTreeItem[],
  searchQuery: string,
): FolderTreeItem[] {
  const query = searchQuery.trim().toLowerCase();
  if (!query) return subfolders;
  return subfolders.filter((subfolder) =>
    subfolder.name?.toLowerCase().includes(query),
  );
}
